import { Button, Checkbox, Container, FormControlLabel, FormGroup, Grid, SpeedDial, SpeedDialIcon } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CustomDialog from "../components/CustomDialog";
import LazyLoader from "../components/LazyLoader";
import SearchField from "../components/SearchField";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import ProductDetails from "./helper/ProductDetails";
import ProductListElement from "./helper/ProductListElement";

/**
 * Implements search of products on 3Dfindit.
 * @returns 
 */
export default function Findit3d() {

  const { locale } = useLanguage();

  const intl = useIntl();
  const api = useApi();

  // the search-query
  const [query, setQuery] = useState("");

  // all catalogs that the user can filter for
  const [manufacturers, setManufacturers] = useState([
    {
      title: "Ascendor",
      search: "ascendor",
      selected: false
    },
    {
      title: "Kaiser",
      search: "kaiser",
      selected: false
    },
    {
      title: "Hargassner",
      search: "hargassner",
      selected: false
    },
    {
      title: "Butzbach",
      search: "butzbach",
      selected: false
    },
    {
      title: "Palme",
      search: "palme",
      selected: false
    },
    {
      title: "Kermi",
      search: "kermi",
      selected: false
    },
    {
      title: "Tece",
      search: "tece",
      selected: false
    },
    {
      title: "Duscholux",
      search: "duscholux",
      selected: false
    },
    {
      title: "Kingspan",
      search: "kingspan,kingspan_insulation,kingspan_kla",
      selected: false
    },
    {
      title: "Schell",
      search: "schell",
      selected: false
    },
    {
      title: "Brötje",
      search: "broetje",
      selected: false
    },
    {
      title: "Rittal",
      search: "rittal",
      selected: false
    },
    {
      title: "Dimplex",
      search: "dimplex",
      selected: false
    },
    {
      title: "Sanitas Trösch",
      search: "sanitas_troesch",
      selected: false
    },
    {
      title: "Kessler",
      search: "kessler",
      selected: false
    },
    {
      title: "Artweger",
      search: "artweger",
      selected: false
    },
    {
      title: "Inda",
      search: "inda",
      selected: false
    },
    {
      title: "Kaldewei",
      search: "kaldewei",
      selected: false
    },
    {
      title: "Laufen",
      search: "laufen",
      selected: false
    },
    {
      title: "Bradley",
      search: "bradley",
      selected: false
    },
    {
      title: "CWS Boco",
      search: "cws_boco",
      selected: false
    },
    {
      title: "Kessel",
      search: "kessel",
      selected: false
    },
    {
      title: "Kohler",
      search: "kohler",
      selected: false
    },
    {
      title: "PERI",
      search: "peri",
      selected: false
    },
    {
      title: "Quadro",
      search: "quadro",
      selected: false
    },
    {
      title: "Miele",
      search: "miele",
      selected: false
    },
    {
      title: "Carrier",
      search: "carrier",
      selected: false
    },
    {
      title: "Fröling",
      search: "froeling",
      selected: false
    },
    {
      title: "Wilo",
      search: "wilo",
      selected: false
    },
    {
      title: "Villeroy & Boch",
      search: "villeroy_boch_tiles",
      selected: false
    },
    {
      title: "Ceasefire",
      search: "ceasefire",
      selected: false
    },
    {
      title: "Toshiba",
      search: "toshiba",
      selected: false
    },
    {
      title: "Schneider Electric",
      search: "schneider_electric",
      selected: false
    },
    {
      title: "ETA Heiztechnik",
      search: "eta_heiztechnik",
      selected: false
    },
    {
      title: "Fulton",
      search: "fulton",
      selected: false
    },
    {
      title: "Caleido",
      search: "caleido",
      selected: false
    },
    {
      title: "LG",
      search: "lg,lg_hvac",
      selected: false
    },
    {
      title: "Fujitsu",
      search: "fujitsu",
      selected: false
    },
    {
      title: "Belimo",
      search: "belimo",
      selected: false
    },
    {
      title: "Siemens",
      search: "siemens_industry,siemens,siemens_bt_ap,siemens_bt_eu,siemens_bt_us",
      selected: false
    },
    {
      title: "Novelan",
      search: "novelan",
      selected: false
    },
  ].sort((a, b) => a.title.localeCompare(b.title)));
  // state for showing manufacturer-filter
  const [showManufacturers, setShowManufacturers] = useState(false);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(5);
  // if the client currently fetches data from the server
  const [loading, setLoading] = useState(false);
  // if the end of the products has been reached
  const [endReached, setEndReached] = useState(false);

  // the product-data
  const [data, setData] = useState([]);

  const [openProductDetails, setOpenProductDetails] = useState(null);

  /**
   * Whenever the locale changes, restart the search (because different locales return different search-results) .
   */
  useEffect(() => {
    startSearch(query);
  }, [locale]);

  /**
   * Get the current language from the locale.
   * @returns 
   */
  const getLanguage = () => {
    switch (locale) {
      case "de":
        return "german";
      default:
        return "english";
    }
  };

  /**
   * Starts a new search and fetches products.
   */
  const startSearch = (searchKeyword = query) => {
    // reset page
    setPage(0);
    // reset end-reached-state
    setEndReached(false);
    fetchData(0, searchKeyword);
  };

  /**
   * Fetches the current page of products from the server and appends the data to
   * the already loaded data.
   * @param {*} page 
   * @param {*} searchKeyword 
   */
  const fetchData = (page, searchKeyword = query) => {
    setLoading(true);
    api
      .get(`/3dfindit/filter?search=${searchKeyword}&language=${getLanguage(locale)}&catalogs=${manufacturers.filter(m => m.selected).map(m => m.search).join(",")}&page=${page}&size=${size}`)
      .then(response => {
        if (page === 0) {
          // discard old data
          setData(() => [...response.data.products]);
        } else {
          setData(old => [...old, ...response.data.products]);
        }
        if (response.data.last) {
          // all products fetched
          setEndReached(true);
        }
      })
      .catch(error => {
        console.log(error);
        // error, therefore end reached
        setEndReached(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Fetches data, whenever the page changes, or a search is triggered.
   * Only fetches additional pages, not the first page.
   */
  useEffect(() => {
    if (page === 0) return;
    fetchData(page, query);
  }, [page]);

  /**
   * Whenever the query is changed, or a manufacturer is selected / deselected, start a new search.
   */
  useEffect(() => {
    startSearch();
  }, [query, manufacturers]);

  /**
   * Should be called when a manufacturer-checkbox changes its state.
   * @param {*} title 
   * @returns 
   */
  const handleManufacturerChange = (title) => (event) => {
    setManufacturers(old => old.map(m => {
      if (m.title === title) {
        m.selected = event.target.checked;
      }
      return m;
    }));
  };

  const productElements = useMemo(() => {
    return data.map((item, index) => {
      return <ProductListElement
        key={index}
        path={item.path}
        name={item.name}
        imagePath={item.imagePath}
        catalogName={item.catalogName}
        onClick={() => setOpenProductDetails({ ...item })}
      />
    })
  }, [data]);

  return (
    <Container>
      <h3><FormattedMessage id="ImportFindit3d.heading" /> <a target="_blank" href="https://www.3dfindit.com" >3Dfindit</a></h3>

      <SearchField onChange={value => setQuery(value)} onEnter={startSearch} />

      <h4><FormattedMessage id="ImportFindit3d.manufacturers" /></h4>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} >
        <SpeedDial
          ariaLabel="SpeedDial controlled open example"
          style={{ marginBottom: 10, marginTop: -20 }}
          icon={<SpeedDialIcon />}
          onClick={() => setShowManufacturers(old => !old)}
          open={showManufacturers}
        >
        </SpeedDial>

        {showManufacturers && <FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
          <Grid container spacing={2} >
            {manufacturers.map((m, index) => {
              return <Grid key={index} item container xs={6} sm={3} md={2} >
                <FormControlLabel label={m.title} control={<Checkbox checked={m.selected} onChange={handleManufacturerChange(m.title)} />} />
              </Grid>
            })}
          </Grid>
        </FormGroup>}
      </div>

      <Button sx={{
        marginBottom: 2,
        marginTop: 1
      }} onClick={() => startSearch()} variant="contained" ><FormattedMessage id="ImportFindit3d.search" /></Button>

      <LazyLoader
        onLoad={() => setPage(prev => prev + 1)}
        loading={loading}
        endReached={endReached}
      >
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }} >
          {productElements}
        </div>
      </LazyLoader>

      <CustomDialog
        title={intl.formatMessage({ id: "ImportFindit3d.chooseProduct" })}
        content={<ProductDetails item={{ ...openProductDetails }} />}
        isOpen={openProductDetails !== null}
        handleAccept={() => { }}
        handleClose={() => setOpenProductDetails(null)}
        maxWidth={"xl"}
        hideConfirmation={true}
        displayClose={true}
      />

    </Container>
  );
};